const CANONICAL_FIELD_KEYS = [
  'name',
  'company_name',
  'email',
  'phone_number',
  'inquiry_type',
  'message',
] as const;

type CanonicalFieldKey = (typeof CANONICAL_FIELD_KEYS)[number];
type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const CANONICAL_FIELD_KEY_SET = new Set<string>(CANONICAL_FIELD_KEYS);

const AUTOCOMPLETE_ALIASES: Readonly<Record<string, CanonicalFieldKey>> = {
  name: 'name',
  'given-name': 'name',
  'family-name': 'name',
  organization: 'company_name',
  email: 'email',
  tel: 'phone_number',
  'tel-country-code': 'phone_number',
  'tel-national': 'phone_number',
  'tel-area-code': 'phone_number',
  'tel-local': 'phone_number',
};

const SEMANTIC_ALIASES: Readonly<Record<CanonicalFieldKey, readonly string[]>> = {
  name: ['name', 'fullname', 'yourname', 'お名前', 'ご氏名', '氏名'],
  company_name: [
    'company',
    'companyname',
    'organization',
    'organizationname',
    '会社名',
    '企業名',
    '法人名',
  ],
  email: ['email', 'emailaddress', 'メール', 'メールアドレス'],
  phone_number: [
    'phone',
    'phonenumber',
    'tel',
    'telephone',
    'mobile',
    '電話',
    '電話番号',
    '携帯電話',
    '携帯電話番号',
  ],
  inquiry_type: [
    'inquirytype',
    'contacttype',
    'お問い合わせ種別',
    '問い合わせ種別',
    '当サービスをどこで知りましたか',
  ],
  message: [
    'message',
    'inquirydetails',
    'contactmessage',
    'お問い合わせ内容',
    '問い合わせ内容',
    'ご相談内容',
  ],
};

const NORMALIZED_ALIAS_TO_KEY = new Map<string, CanonicalFieldKey>(
  Object.entries(SEMANTIC_ALIASES).flatMap(([key, aliases]) =>
    aliases.map((alias) => [alias, key as CanonicalFieldKey] as const),
  ),
);

type FieldMatch = {
  key: CanonicalFieldKey;
  priority: number;
};

function normalizeSemanticMetadata(value: string | null | undefined): string {
  return (
    value
      ?.normalize('NFKC')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]/gu, '') ?? ''
  );
}

function canonicalFieldKey(value: string | null): CanonicalFieldKey | null {
  return value && CANONICAL_FIELD_KEY_SET.has(value) ? (value as CanonicalFieldKey) : null;
}

function associatedLabelText(field: FormControl): string | null {
  for (const label of field.form?.querySelectorAll('label') ?? []) {
    if (label.control === field) return label.textContent;
  }
  return null;
}

function classifyControl(field: FormControl): FieldMatch | null {
  const explicit = canonicalFieldKey(field.getAttribute('data-vlt-field'));
  if (explicit) return { key: explicit, priority: 3 };

  if (field instanceof HTMLInputElement) {
    const inputType = field.type.toLowerCase();
    if (inputType === 'email') return { key: 'email', priority: 2 };
    if (inputType === 'tel') return { key: 'phone_number', priority: 2 };
  }

  for (const token of field.autocomplete.toLowerCase().split(/\s+/)) {
    const key = AUTOCOMPLETE_ALIASES[token];
    if (key) return { key, priority: 2 };
  }

  const metadata = [
    field.name,
    field.id,
    field.getAttribute('aria-label'),
    associatedLabelText(field),
    field.getAttribute('placeholder'),
  ];
  for (const value of metadata) {
    const key = NORMALIZED_ALIAS_TO_KEY.get(normalizeSemanticMetadata(value));
    if (key) return { key, priority: 1 };
  }

  return null;
}

export function addCanonicalFormFields(form: HTMLFormElement, data: Record<string, unknown>): void {
  const protectedKeys = new Set(
    CANONICAL_FIELD_KEYS.filter((key) => Object.prototype.hasOwnProperty.call(data, key)),
  );
  const inferredPriorities = new Map<CanonicalFieldKey, number>();

  for (const field of form.elements) {
    if (
      !(
        field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement ||
        field instanceof HTMLTextAreaElement
      ) ||
      !field.name ||
      !Object.prototype.hasOwnProperty.call(data, field.name)
    ) {
      continue;
    }

    const match = classifyControl(field);
    if (!match || protectedKeys.has(match.key)) continue;

    const currentPriority = inferredPriorities.get(match.key) ?? 0;
    if (match.priority <= currentPriority) continue;

    data[match.key] = data[field.name];
    inferredPriorities.set(match.key, match.priority);
  }
}
